import React, { useEffect, useMemo, useState } from 'react';
import { Package, Box, Plus, Trash2, ChevronLeft, ChevronRight, Send, Pencil, Edit2, X } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button } from '../../../../../components/ui/Button';
import { PendingButton } from '../../../../../components/ui/PendingButton';
import { ConfirmDialog } from '../../../../../components/ui/ConfirmDialog';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { DELIVERY_STATUSES, STATUS_MAP } from '../../../../../utils/catalogs';
import { toPublicLabelUrl } from '../../../../../utils/s3Utils';
import {
  AWAITING_DISPATCH_STATUSES,
  EDITABLE_ITEM_STATUSES,
  MULTI_LABEL_COURIERS,
  itemHasLogistics,
  sumItemVolumes,
  sumItemWeights,
} from '../../../../../features/deliveryItems/deliveryItemsConstants';
import {
  fetchDeliveryItems,
  createDeliveryItem,
  deleteDeliveryItem,
  updateDeliveryItemStatus,
} from '../../../../../features/deliveryItems/deliveryItemsThunks';
import { setPaginationPage, setPaginationRowsPerPage } from '../../../../../features/deliveryItems/deliveryItemsSlice';
import { dispatchPreparedOrder } from '../../../../../features/orderDetails/orderDetailsThunks';
import { selectHasFulfillmentPro } from '../../../../../features/auth/authSelectors';
import EditLogisticsModal from '../modals/EditLogisticsModal';
import SetItemsCountModal from '../modals/SetItemsCountModal';

/**
 * Opens a label in a new tab.
 *
 * A navigation is not subject to CORS, so this one would also work with the URL
 * as the API returns it. It goes through the same helper as the downloads so
 * that every label in the platform is asked for at one address.
 */
const openLabel = (labelUrl) => {
  if (!labelUrl) return;
  window.open(toPublicLabelUrl(labelUrl), '_blank', 'noopener,noreferrer');
};

/**
 * Every status a bulto can be moved to.
 *
 * The same catalog the order-level editor uses: the backend keeps one enum for
 * deliveries and their items, and the legacy platform's separate list for items
 * had already drifted out of it — a bulto in `assigned` displayed correctly but
 * could not be edited, because that option was missing from the editor's list.
 */
const STATUS_OPTIONS = DELIVERY_STATUSES.map((status) => ({
  value: status.system_name,
  label: status.user_name,
}));

const OrderPackagesTab = ({ order }) => {
  const dispatch = useDispatch();
  const { orderId } = useParams();

  const {
    data: deliveryItems,
    loading,
    error,
    pagination,
    count: deliveryItemsCount,
  } = useSelector((state) => state.deliveryItems);
  const hasFulfillmentPro = useSelector(selectHasFulfillmentPro);

  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingLogistics, setEditingLogistics] = useState(null);
  const [settingCount, setSettingCount] = useState(false);
  const [actionError, setActionError] = useState('');
  // One row at a time: a single id rather than a flag per row.
  const [editingStatusId, setEditingStatusId] = useState(null);

  useEffect(() => {
    if (orderId) {
      dispatch(fetchDeliveryItems(orderId));
    }
  }, [dispatch, orderId, pagination.page, pagination.rowsPerPage]);

  const deliveryStatus = order?.delivery?.status;
  const canEditItems = EDITABLE_ITEM_STATUSES.includes(deliveryStatus);
  const isAwaitingDispatch = AWAITING_DISPATCH_STATUSES.includes(deliveryStatus);
  const usesMultiLabel = MULTI_LABEL_COURIERS.includes(
    order?.delivery?.shipping_zone?.courier?.toLowerCase()
  );

  const totals = useMemo(() => {
    const ready = deliveryItems.filter(itemHasLogistics).length;
    return {
      weight: sumItemWeights(deliveryItems),
      volume: sumItemVolumes(deliveryItems),
      ready,
      allReady: deliveryItems.length > 0 && ready === deliveryItems.length,
    };
  }, [deliveryItems]);

  const handleAdd = async () => {
    setActionError('');
    setAdding(true);
    try {
      await dispatch(createDeliveryItem({ orderId, item: { volumetric_weight: 1000 } })).unwrap();
    } catch (thunkError) {
      setActionError(thunkError);
    } finally {
      setAdding(false);
    }
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await dispatch(deleteDeliveryItem({ itemId: itemToDelete.id, orderId })).unwrap();
      setItemToDelete(null);
    } catch (thunkError) {
      setActionError(thunkError);
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (item, status) => {
    setEditingStatusId(null);
    if (status === item.status) return;

    setActionError('');
    try {
      await dispatch(updateDeliveryItemStatus({ itemId: item.id, status, orderId })).unwrap();
    } catch (thunkError) {
      setActionError(thunkError);
    }
  };

  /** Generates the tracking and hands the parcels over to the courier. */
  const handleDispatch = async () => {
    if (!order?.delivery?.id) return;
    setActionError('');
    setDispatching(true);
    try {
      await dispatch(dispatchPreparedOrder({ orderId, deliveryId: order.delivery.id })).unwrap();
      dispatch(fetchDeliveryItems(orderId));
    } catch (thunkError) {
      setActionError(thunkError);
    } finally {
      setDispatching(false);
    }
  };

  const from = (pagination.page - 1) * pagination.rowsPerPage + 1;
  const to = Math.min(pagination.page * pagination.rowsPerPage, deliveryItemsCount);

  return (
    <div className="space-y-6 animate-in slide-in-from-bottom-2 duration-300">
      {actionError && (
        <div className="bg-red-50 dark:bg-red-900/20 text-danger px-5 py-3 rounded-2xl border border-red-200 dark:border-red-900 text-sm font-medium flex items-center justify-between gap-4">
          <span>{actionError}</span>
          <button onClick={() => setActionError('')} className="font-bold shrink-0">Cerrar</button>
        </div>
      )}

      <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border overflow-hidden">
        <div className="p-6 border-b border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <h3 className="text-lg font-bold text-ninja-dark dark:text-white flex items-center gap-2">
            <Package className="text-ninja-green" size={20} /> Detalle de Bultos
          </h3>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Both belong to Fulfillment Pro. Adding a parcel does not: the
                legacy offers it on either plan, and only these two live in its
                pro branch. */}
            {hasFulfillmentPro && isAwaitingDispatch && (
              <Button
                variant="outline"
                size="sm"
                icon={Send}
                onClick={handleDispatch}
                disabled={!totals.allReady}
                loading={dispatching}
                loadingLabel="Enviando..."
                title={
                  totals.allReady
                    ? 'Enviar al courier'
                    : 'Completa las variables logísticas de todos los bultos primero'
                }
              >
                Enviar al courier
              </Button>
            )}
            {hasFulfillmentPro && (
              <Button
                variant="ghost"
                size="sm"
                icon={Pencil}
                onClick={() => setSettingCount(true)}
                disabled={!canEditItems}
                title="Cambiar cantidad total de bultos"
              >
                {deliveryItemsCount} bultos
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              icon={Plus}
              onClick={handleAdd}
              disabled={loading || !canEditItems}
              loading={adding}
              loadingLabel="Añadiendo..."
            >
              Añadir Bulto
            </Button>
          </div>
        </div>

        {/* The whole logistics half of this screen is Fulfillment Pro: the
            totals, the parcel rows with their contents and dimensions, and the
            editor they open. Without the addon the legacy shows only the item
            table below, which is what a shipment is without fulfillment. */}
        {hasFulfillmentPro && deliveryItems.length > 0 && (
          <>
            {/* Logistics KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border border-b border-border bg-ninja-gray/50 dark:bg-[#222]">
              <div className="px-6 py-4">
                <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">Peso total</span>
                <p className="font-bold text-ninja-dark dark:text-white mt-1">
                  {totals.weight > 0
                    ? `${totals.weight.toLocaleString('es-CL')} g · ${(totals.weight / 1000).toFixed(2)} kg`
                    : '—'}
                </p>
              </div>
              <div className="px-6 py-4">
                <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">Volumen total</span>
                <p className="font-bold text-ninja-dark dark:text-white mt-1">
                  {totals.volume > 0 ? `${totals.volume.toLocaleString('es-CL')} cm³` : '—'}
                </p>
              </div>
              <div className="px-6 py-4">
                <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">Logística completa</span>
                <p className="font-bold text-ninja-dark dark:text-white mt-1">
                  {totals.ready} / {deliveryItems.length} bultos
                </p>
                <div className="h-1 bg-border rounded-full mt-2 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${totals.allReady ? 'bg-ninja-green' : 'bg-amber-400'}`}
                    style={{ width: `${(totals.ready / deliveryItems.length) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Per-bulto contents */}
            <div className="overflow-x-auto border-b border-border">
              <table data-testid="order-packages-contents-table" className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-ninja-gray/50 dark:bg-[#222]">
                    <th className="px-6 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider w-20">Bulto</th>
                    <th className="px-6 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Productos</th>
                    <th className="px-6 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Peso</th>
                    <th className="px-6 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Dimensiones</th>
                    <th className="px-6 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider text-center">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {deliveryItems.map((item) => {
                    const ready = itemHasLogistics(item);
                    const contents = (item.item_products || [])
                      .map((assignment) => {
                        const product = (order.products || []).find(
                          (candidate) => candidate.id === assignment.order_product
                        );
                        return product
                          ? `${product.product?.name} ×${assignment.prepared_quantity}`
                          : null;
                      })
                      .filter(Boolean);

                    return (
                      <tr
                        key={item.id}
                        onClick={() => isAwaitingDispatch && setEditingLogistics(item)}
                        className={`transition-colors ${isAwaitingDispatch ? 'cursor-pointer hover:bg-ninja-gray/30 dark:hover:bg-[#1f1f1f]' : ''}`}
                      >
                        <td className="px-6 py-3 font-bold text-ninja-dark dark:text-white">#{item.item_index}</td>
                        <td className="px-6 py-3">
                          {contents.length > 0 ? (
                            <div className="space-y-0.5">
                              {contents.map((line) => (
                                <p key={line} className="text-sm text-text-secondary">{line}</p>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-text-muted italic">Sin asignar</span>
                          )}
                        </td>
                        <td className="px-6 py-3 text-sm text-text-secondary">
                          {item.weight_grams ? `${item.weight_grams.toLocaleString('es-CL')} g` : '—'}
                        </td>
                        <td className="px-6 py-3 text-sm text-text-secondary">
                          {ready ? `${item.height_cm}×${item.width_cm}×${item.length_cm} cm` : '—'}
                        </td>
                        <td className="px-6 py-3 text-center">
                          <span
                            className={`px-2 py-1 rounded-md text-xs font-bold uppercase tracking-wider ${ready
                              ? 'bg-ninja-greenLight text-ninja-green'
                              : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                              }`}
                          >
                            {ready ? 'Listo' : 'Pendiente'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {loading && deliveryItems.length === 0 ? (
          <div className="p-12 text-center text-text-muted">Cargando bultos...</div>
        ) : error ? (
          <div className="p-12 text-center text-red-500">{error}</div>
        ) : deliveryItems.length === 0 ? (
          <div className="p-12 flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-ninja-gray dark:bg-[#2d2d2d] rounded-full flex items-center justify-center mb-4">
              <Box size={24} className="text-text-muted" />
            </div>
            <h3 className="text-lg font-bold text-ninja-dark dark:text-white mb-2">Sin Bultos Registrados</h3>
            <p className="text-text-secondary max-w-sm">Esta orden no tiene productos o bultos asociados en este momento.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="order-packages-table" className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-ninja-gray/50 dark:bg-[#222]">
                  <th className="px-6 py-4 text-xs font-semibold text-text-muted uppercase tracking-wider">ID Item</th>
                  <th className="px-6 py-4 text-xs font-semibold text-text-muted uppercase tracking-wider">Índice</th>
                  <th className="px-6 py-4 text-xs font-semibold text-text-muted uppercase tracking-wider">Tracking</th>
                  <th className="px-6 py-4 text-xs font-semibold text-text-muted uppercase tracking-wider text-center">Estado</th>
                  <th className="px-6 py-4 text-xs font-semibold text-text-muted uppercase tracking-wider text-center">Etiqueta</th>
                  <th className="px-6 py-4 text-xs font-semibold text-text-muted uppercase tracking-wider text-center">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {deliveryItems.map((item, index) => (
                  <tr key={item.id || index} className="hover:bg-ninja-gray/30 dark:hover:bg-[#1f1f1f] transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-ninja-dark dark:text-white">{item.id}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-text-secondary">Bulto {item.item_index} de {item.total_items}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-text-secondary bg-ninja-gray dark:bg-[#2d2d2d] px-2 py-1 rounded-md">
                        {item.tracking_number || 'Pendiente...'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {editingStatusId === item.id ? (
                        <div className="flex items-center justify-center gap-1">
                          <FilterableSelect
                            variant="filter"
                            value={item.status}
                            onChange={(status) => handleStatusChange(item, status)}
                            options={STATUS_OPTIONS}
                            searchPlaceholder="Buscar estado..."
                          />
                          <Button
                            variant="subtle"
                            size="sm"
                            icon={X}
                            iconOnly
                            onClick={() => setEditingStatusId(null)}
                            aria-label="Cancelar el cambio de estado"
                          />
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-2">
                          <span className="bg-ninja-greenLight text-ninja-green px-2 py-1 rounded-md text-xs font-bold uppercase tracking-wider">
                            {STATUS_MAP[item.status]?.user_name || item.status || 'N/A'}
                          </span>
                          <PendingButton
                            pendingOn={updateDeliveryItemStatus}
                            pendingKey={item.id}
                            variant="subtle"
                            size="sm"
                            icon={Edit2}
                            iconOnly
                            onClick={() => setEditingStatusId(item.id)}
                            aria-label={`Editar el estado del bulto ${item.id}`}
                            className="bg-ninja-gray/50 hover:!text-ninja-green hover:!bg-ninja-greenLight"
                          />
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {item.label ? (
                        <button
                          type="button"
                          onClick={() => openLabel(item.label)}
                          className="text-sm text-ninja-green italic hover:underline"
                        >
                          {usesMultiLabel ? 'Ver todas las etiquetas' : 'Ver Etiqueta'}
                        </button>
                      ) : (
                        <span className="text-sm text-text-muted italic">
                          {usesMultiLabel ? '-' : 'Procesando etiqueta...'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="subtle"
                          size="sm"
                          icon={Trash2}
                          iconOnly
                          onClick={() => setItemToDelete(item)}
                          disabled={deliveryItems.length <= 1 || !canEditItems}
                          aria-label={`Eliminar el bulto ${item.id}`}
                          title={
                            deliveryItems.length <= 1
                              ? 'No puedes eliminar el único bulto'
                              : 'Eliminar bulto'
                          }
                          className="hover:!text-danger hover:!bg-red-50 dark:hover:!bg-red-500/10"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="flex items-center justify-end px-6 py-4 border-t border-border/60 gap-4 text-sm text-text-secondary">
              <div className="flex items-center gap-2">
                <span>Rows per page:</span>
                <select
                  value={pagination.rowsPerPage}
                  onChange={(e) => dispatch(setPaginationRowsPerPage(Number(e.target.value)))}
                  className="bg-transparent border-none text-ninja-dark dark:text-white font-medium focus:ring-0 cursor-pointer"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div>
                {deliveryItemsCount === 0 ? '0-0 of 0' : `${from}-${to} of ${deliveryItemsCount}`}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="subtle"
                  size="sm"
                  icon={ChevronLeft}
                  iconOnly
                  disabled={pagination.page <= 1}
                  onClick={() => dispatch(setPaginationPage(pagination.page - 1))}
                  aria-label="Página anterior"
                />
                <Button
                  variant="subtle"
                  size="sm"
                  icon={ChevronRight}
                  iconOnly
                  disabled={to >= deliveryItemsCount}
                  onClick={() => dispatch(setPaginationPage(pagination.page + 1))}
                  aria-label="Página siguiente"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <SetItemsCountModal
        isOpen={settingCount}
        onClose={() => setSettingCount(false)}
        currentCount={deliveryItemsCount}
        orderId={orderId}
        onError={setActionError}
      />

      <EditLogisticsModal
        item={editingLogistics}
        onClose={() => setEditingLogistics(null)}
        order={order}
        onError={setActionError}
      />

      <ConfirmDialog
        isOpen={Boolean(itemToDelete)}
        onClose={() => setItemToDelete(null)}
        onConfirm={handleConfirmDelete}
        loading={deleting}
        title="Eliminar bulto"
        message={`Se eliminará el bulto ${itemToDelete?.id ?? ''}. Las etiquetas se regenerarán y los bultos se re-indexarán. Esta acción no se puede deshacer.`}
      />
    </div>
  );
};

export default OrderPackagesTab;
